/**
 * Files on a Monday update / reply (John Garcia, 28 Oct 2026: "screenshots shared by UW not viewable").
 * Images render as thumbnails straight in the thread; everything else is a chip. Click → in-portal preview.
 */
import { useEffect, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import apiClient, { mirrorFileViewUrl } from "@/lib/api";

const IMG_EXT = /^\.?(png|jpe?g|gif|webp|bmp|heic)$/i;
export const isImageFile = (f) => IMG_EXT.test(f?.ext || "") || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(f?.name || "");

function Thumb({ file, onOpen }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    apiClient.get("/applications/mirror/files/view", { params: { src: file.url, name: file.name || "file" }, responseType: "blob", timeout: 60000 })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file.url, file.name]);
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(file); }} title={`${file.name} — click to view`}
      className="relative h-24 max-w-[200px] rounded-md border bg-white overflow-hidden hover:ring-2 hover:ring-blue-300 transition-shadow"
      data-testid={`mirror-update-image-${file.id}`}>
      {src ? <img src={src} alt={file.name} className="h-full w-auto object-contain" />
        : <span className="flex h-full w-28 items-center justify-center text-slate-400">{failed ? <span className="text-[10px] px-2">couldn't load</span> : <Loader2 className="w-4 h-4 animate-spin" />}</span>}
      <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1.5 py-0.5 text-left text-[9px] text-white">{file.name}</span>
    </button>
  );
}

export function MirrorFileChips({ files, onOpen, testPrefix = "mirror-update-file" }) {
  if (!files?.length) return null;
  const images = files.filter(isImageFile), others = files.filter((f) => !isImageFile(f));
  return (
    <div className="mt-1.5 space-y-1.5">
      {images.length > 0 && <div className="flex flex-wrap gap-1.5">{images.map((f) => <Thumb key={f.id} file={f} onOpen={onOpen} />)}</div>}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {others.map((f) => (
            <a key={f.id} href={mirrorFileViewUrl(f.url, f.name, f.tok)} target="_blank" rel="noreferrer"
              onClick={(e) => { if (e.button === 0 && !e.metaKey && !e.ctrlKey) { e.preventDefault(); e.stopPropagation(); onOpen(f); } }}
              className="text-[10px] flex items-center gap-1 bg-white border rounded px-1.5 py-0.5 hover:bg-slate-50"
              title="Preview here (Ctrl/Cmd-click opens in a new tab)"
              data-testid={`${testPrefix}-${f.id}`}>
              <Eye className="w-2.5 h-2.5 text-slate-400" />{f.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
